import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "@core/context/NavigationContext";
import { cn } from "@shared/lib/utils";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { InfoTooltip } from "@shared/ui/infoTooltip";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@shared/ui/tooltip";
import { Switch } from "@shared/ui/switch";
import { Building } from "lucide-react";
import UtilityActionsMenu from "./UtilityActionsMenu";
import ViewUtilityDetailsModal from "./UtilityDetailDialog";

interface UtilityCardProps {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  setupProgress: number;
  className?: string;
  onActivateToggle?: (id: string, isActive: boolean) => void;
  onEditUtility?: (id: string) => void;
  utility: any;
}

const UtilityCard: React.FC<UtilityCardProps> = ({
  id,
  name,
  description,
  isActive,
  setupProgress,
  className,
  onActivateToggle,
  onEditUtility,
  utility,
}) => {
  console.log("UtilityCard rendered with description:", description);
  const [openView, setOpenView] = useState(false);
  const handleToggleActive = (checked: boolean) => {
    onActivateToggle?.(id, checked);
  };
  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEditUtility?.(id);
  };

  // const handleCardClick = () => {

  // navigate("/onboarding/utility-setup/utility-admin-dashboard");
  // };
  const handleCardClick = () => {
    // Save full utility object
    localStorage.setItem("utility", JSON.stringify(utility));

    // Hard redirect (preserves reload)
    window.location.href = "/utility/utility-setup/utility-admin-dashboard";
  };

  return (
    <Card
      className={cn(
        "overflow-hidden transition-all duration-300 hover:shadow-md h-full relative",
        className
      )}
    >
      {/* Header */}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <div className=" rounded-md text-primary flex items-center justify-center">
      {utility?.logo ? (
        <img
          src={utility.logo}
          alt={`${name} logo`}
          className="h-10 w-10 object-contain"
        />
      ) : (
        <Building className="h-5 w-5" />
      )}
    </div>
            <CardTitle className="text-lg font-medium">{name}</CardTitle>
          </div>

          <div className="flex items-center gap-1">
            <Badge
              variant={isActive ? "default" : "outline"}
              className={cn(
                "text-xs px-2 py-0.5 rounded-full font-semibold",
                isActive
                  ? "bg-green-100 text-green-700 border-green-300"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              )}
            >
              {isActive ? "Active" : "Inactive"}
            </Badge>
            <UtilityActionsMenu
              isActive={isActive}
              onView={() => setOpenView(true)}
              onEdit={() => onEditUtility?.(id)}
              onToggleStatus={() => onActivateToggle?.(id, !isActive)}
            />
          </div>
        </div>
      {description && (
        <TooltipProvider>
          <Tooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <div className="cursor-default w-fit">
                <CardDescription className="text-sm max-w-[200px] truncate">
                  {description}
                </CardDescription>
              </div>
            </TooltipTrigger>
            <TooltipContent align="start" className="max-w-[300px] break-words whitespace-normal z-50">
              <p className="text-sm">{description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      </CardHeader>

      {/* Content - Progress */}
      <CardContent className="pb-2">
        <div className="flex justify-between items-center text-sm mb-2">
          <div className="flex items-center gap-1">
            <span>Setup Progress:</span>
            <InfoTooltip content="Complete all required configuration for this utility" />
          </div>
          <span className="font-medium">{setupProgress}%</span>
        </div>

        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 bg-primary"
            style={{ width: `${setupProgress}%` }}
          />
        </div>
      </CardContent>

      {/* Footer - Actions */}
      <CardFooter className="mt-2 p-0">
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs"
          onClick={handleCardClick}
        >
          {setupProgress === 100 ? "Manage Utility" : "Continue Setup"}
        </Button>
      </CardFooter>
      {openView && <ViewUtilityDetailsModal isOpen={openView} onClose={() => setOpenView(false)} utility={utility} />}
    </Card>
  );
};

export default UtilityCard;
